import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import TiptapEditor from './TiptapEditor';
import {
    FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiChevronUp, FiChevronDown,
    FiEyeOff, FiEye, FiImage, FiList, FiMapPin, FiPhone, FiMail,
    FiBriefcase, FiPrinter, FiMessageSquare, FiSend, FiMenu, FiLayers, FiMessageCircle
} from 'react-icons/fi';

export default function AdminContactManager() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        type: 'hero',
        title: '',
        subtitle: '',
        content: '',
        data: null,
        btn_text: '',
        btn_link: '',
        order: 0,
        is_published: true
    });

    const sectionTypes = [
        { value: 'hero', label: 'En-tête (Hero)' },
        { value: 'info_form', label: 'Infos & Formulaire de contact' },
        { value: 'service_tiles', label: 'Tuiles des Services' },
        { value: 'map', label: 'Carte Google Maps' }
    ];

    const [siteSettings, setSiteSettings] = useState(null);
    const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

    const fetchSiteSettings = async () => {
        try {
            const records = await pb.collection('site_settings').getList(1, 1);
            if (records.items.length > 0) {
                setSiteSettings(records.items[0]);
            }
        } catch (error) {
            console.error('Error fetching site settings:', error);
        }
    };

    const fetchSections = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('contact_sections').getFullList({
                sort: 'order',
            });
            setSections(records);
        } catch (error) {
            console.error('Error fetching contact sections:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSections();
        fetchSiteSettings();
    }, []);

    const handleToggleContact = async () => {
        if (!siteSettings) return;
        setIsUpdatingSettings(true);
        try {
            const updated = await pb.collection('site_settings').update(siteSettings.id, {
                show_contact: !siteSettings.show_contact
            });
            setSiteSettings(updated);
            // Optionally reload to propagate context if needed, 
            // but usually a simple update is enough for next page load or polling.
        } catch (error) {
            alert('Erreur lors de la mise à jour des paramètres');
        } finally {
            setIsUpdatingSettings(false);
        }
    };

    const handleEdit = (section) => {
// ... existing handleEdit code ...
        setEditingId(section.id);

        // Migration des données pour service_tiles
        let processedData = section.data;
        if (section.type === 'service_tiles' && Array.isArray(processedData)) {
            processedData = processedData.map(item => ({
                ...item,
                phones: Array.isArray(item.phones) ? item.phones : (item.phone ? [item.phone] : ['']),
                mails: Array.isArray(item.mails) ? item.mails : (item.mail ? [item.mail] : ['']),
                fax: item.fax || '',
                description: item.description || ''
            }));
        }

        setFormData({
            name: section.name || '',
            type: section.type || 'hero',
            title: section.title || '',
            subtitle: section.subtitle || '',
            content: section.content || '',
            data: processedData || (['service_tiles'].includes(section.type) ? [] : null),
            btn_text: section.btn_text || '',
            btn_link: section.btn_link || '',
            order: section.order || 0,
            is_published: section.is_published !== false
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormData({
            name: '',
            type: 'hero',
            title: '',
            subtitle: '',
            content: '',
            data: null,
            btn_text: '',
            btn_link: '',
            order: sections.length,
            is_published: true
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (editingId && editingId !== 'new') {
                await pb.collection('contact_sections').update(editingId, formData);
            } else {
                await pb.collection('contact_sections').create({
                    ...formData,
                    order: sections.length
                });
            }
            handleCancel();
            fetchSections();
        } catch (error) {
            alert('Erreur lors de la sauvegarde : ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer cette section ?')) {
            try {
                await pb.collection('contact_sections').delete(id);
                fetchSections();
            } catch (error) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    const handleMove = async (index, direction) => {
        const newSections = [...sections];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= sections.length) return;

        [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];

        try {
            await Promise.all(newSections.map((s, i) =>
                pb.collection('contact_sections').update(s.id, { order: i })
            ));
            fetchSections();
        } catch (error) {
            console.error('Reorder error:', error);
        }
    };

    const togglePublish = async (section) => {
        try {
            await pb.collection('contact_sections').update(section.id, {
                is_published: !section.is_published
            });
            fetchSections();
        } catch (error) {
            console.error('Error toggling publish:', error);
        }
    };

    const addDataItem = (type) => {
        const newData = [...(formData.data || [])];
        if (type === 'service_tiles') {
            newData.push({
                title: 'Nouveau Service',
                phones: [''],
                fax: '',
                mails: [''],
                description: ''
            });
        }
        setFormData({ ...formData, data: newData });
    };

    const updateDataItem = (index, field, value) => {
        const newData = [...(formData.data || [])];
        newData[index] = { ...newData[index], [field]: value };
        setFormData({ ...formData, data: newData });
    };

    const updateNestedDataItem = (itemIndex, arrayField, valueIndex, value) => {
        const newData = [...(formData.data || [])];
        const currentItem = { ...newData[itemIndex] };
        const newArray = [...(currentItem[arrayField] || [''])];
        newArray[valueIndex] = value;
        currentItem[arrayField] = newArray;
        newData[itemIndex] = currentItem;
        setFormData({ ...formData, data: newData });
    };

    const addNestedDataItem = (itemIndex, arrayField) => {
        const newData = [...(formData.data || [])];
        const currentItem = { ...newData[itemIndex] };
        const newArray = [...(currentItem[arrayField] || [])];
        newArray.push('');
        currentItem[arrayField] = newArray;
        newData[itemIndex] = currentItem;
        setFormData({ ...formData, data: newData });
    };

    const removeNestedDataItem = (itemIndex, arrayField, valueIndex) => {
        const newData = [...(formData.data || [])];
        const currentItem = { ...newData[itemIndex] };
        const newArray = [...(currentItem[arrayField] || [])];
        newArray.splice(valueIndex, 1);
        currentItem[arrayField] = newArray;
        newData[itemIndex] = currentItem;
        setFormData({ ...formData, data: newData });
    };

    const removeDataItem = (index) => {
        const newData = [...(formData.data || [])];
        newData.splice(index, 1);
        setFormData({ ...formData, data: newData });
    };

    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.target.style.opacity = '0.5';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItemIndex === index) return;
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedItemIndex(null);
    };

    if (loading && sections.length === 0) return <div className="admin-loading">Chargement...</div>;

    return (
        <div className="admin-content-inner">
            <div className="admin-header-actions" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h2 className="admin-page-title">Gestion du Contact</h2>
                    <p className="admin-page-subtitle">Pilotez le contenu, l'ordre et la visibilité des sections de la page contact.</p>
                </div>
                {!editingId && (
                    <button className="btn btn-primary" onClick={() => setEditingId('new')}>
                        <FiPlus /> Ajouter une section
                    </button>
                )}
            </div>

            {/* Global Settings Toggle */}
            <div className="settings-card slide-up" style={{
                background: siteSettings?.show_contact ? 'linear-gradient(135deg, #dcfce7, #f0fdf4)' : 'linear-gradient(135deg, #fee2e2, #fef2f2)',
                padding: '1.5rem 2rem',
                borderRadius: '24px',
                marginBottom: '2.5rem',
                border: '1px solid',
                borderColor: siteSettings?.show_contact ? '#86efac' : '#fecaca',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '15px',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        color: siteSettings?.show_contact ? '#10b981' : '#ef4444'
                    }}>
                        {siteSettings?.show_contact ? <FiEye /> : <FiEyeOff />}
                    </div>
                    <div>
                        <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Visibilité de la page Contact</h4>
                        <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' }}>
                            {siteSettings?.show_contact
                                ? 'La page est actuellement ACTIVE et visible dans les menus.'
                                : 'La page est actuellement MASQUÉE et les liens sont retirés.'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleToggleContact}
                    disabled={isUpdatingSettings || !siteSettings}
                    className={`btn ${siteSettings?.show_contact ? 'btn-danger' : 'btn-success'}`}
                    style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '12px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        minWidth: '180px',
                        justifyContent: 'center'
                    }}
                >
                    {isUpdatingSettings ? 'Mise à jour...' : (
                        siteSettings?.show_contact ? <><FiEyeOff /> Masquer la page</> : <><FiEye /> Activer la page</>
                    )}
                </button>
            </div>

            <div className="sections-list mt-8">
                {sections.map((sec, index) => {
                    const typeInfo = sectionTypes.find(t => t.value === sec.type);
                    return (
                        <div
                            key={sec.id}
                            className={`section-admin-item ${!sec.is_published ? 'is-draft' : ''} slide-up ${draggedItemIndex === index ? 'is-dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                        >
                            <div className="section-drag-handle">
                                <FiMenu />
                            </div>
                            <div className="section-drag-controls">
                                <button disabled={index === 0} onClick={(e) => { e.stopPropagation(); handleMove(index, 'up'); }} title="Monter"><FiChevronUp /></button>
                                <span className="order-num">{index + 1}</span>
                                <button disabled={index === sections.length - 1} onClick={(e) => { e.stopPropagation(); handleMove(index, 'down'); }} title="Descendre"><FiChevronDown /></button>
                            </div>

                            <div className="section-icon-preview">
                                {sec.type === 'hero' && <FiLayers />}
                                {sec.type === 'info_form' && <FiMessageSquare />}
                                {sec.type === 'service_tiles' && <FiList />}
                                {sec.type === 'map' && <FiMapPin />}
                            </div>

                            <div className="section-info">
                                <div className="section-meta">
                                    <span className="section-badge">{typeInfo?.label || sec.type}</span>
                                    {!sec.is_published && <span className="draft-badge"><FiEyeOff /> Masqué</span>}
                                </div>
                                <h4 className="section-name">{sec.name || 'Sans nom'}</h4>
                                <p className="section-excerpt">{sec.title || 'Pas de titre affiché'}</p>
                            </div>
                            <div className="section-item-actions">
                                <button
                                    className={`btn-icon-round ${!sec.is_published ? 'is-draft-btn' : 'is-published-btn'}`}
                                    onClick={() => togglePublish(sec)}
                                    title={sec.is_published ? "Dépublier" : "Publier"}
                                >
                                    {sec.is_published ? <FiEye /> : <FiEyeOff />}
                                </button>
                                <button className="btn-icon-round" onClick={() => handleEdit(sec)} title="Modifier"><FiEdit2 /></button>
                                <button className="btn-icon-round delete" onClick={() => handleDelete(sec.id)} title="Supprimer"><FiTrash2 /></button>
                            </div>
                        </div>
                    );
                })}
                {sections.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '2px dashed var(--border-subtle)' }}>
                        <p>Aucune section configurée. Cliquez sur "Ajouter une section" pour commencer.</p>
                    </div>
                )}
            </div>

            {editingId && (
                <div className="admin-modal-overlay fade-in">
                    <div className="admin-modal-container zoom-in">
                        <form onSubmit={handleSave}>
                            <div className="modal-header">
                                <div>
                                    <h3>{editingId === 'new' ? 'Nouvelle Section' : 'Édition de Section'}</h3>
                                    <p className="modal-intro">{formData.name || 'Configuration visuelle'}</p>
                                </div>
                                <button type="button" className="close-modal" onClick={handleCancel}><FiX /></button>
                            </div>

                            <div className="modal-body custom-scrollbar">
                                <div className="admin-form-grid">
                                    <div className="form-group">
                                        <label>Nom interne (admin)</label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="ex: Hero principal" required />
                                    </div>

                                    <div className="form-group">
                                        <label>Type de Section</label>
                                        <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value, data: e.target.value === 'service_tiles' ? [] : null })}>
                                            {sectionTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Titre Principal (H2)</label>
                                        <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Sous-titre / Description</label>
                                        <textarea value={formData.subtitle} onChange={e => setFormData({ ...formData, subtitle: e.target.value })} rows={2} />
                                    </div>

                                    {formData.type === 'hero' && (
                                        <div className="form-group full-width">
                                            <div className="data-list-header">
                                                <h4>Boutons d'action</h4>
                                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                                                    const newData = [...(formData.data || [])];
                                                    newData.push({ label: 'Nouveau bouton', link: '/services' });
                                                    setFormData({ ...formData, data: newData });
                                                }}>
                                                    <FiPlus /> Ajouter un bouton
                                                </button>
                                            </div>
                                            <div className="data-items-grid">
                                                {(formData.data || []).map((btn, idx) => (
                                                    <div key={idx} className="data-item-card" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                                        <input
                                                            placeholder="Texte du bouton"
                                                            value={btn.label}
                                                            onChange={e => {
                                                                const newData = [...formData.data];
                                                                newData[idx].label = e.target.value;
                                                                setFormData({ ...formData, data: newData });
                                                            }}
                                                        />
                                                        <input
                                                            placeholder="Lien (ex: /metiers)"
                                                            value={btn.link}
                                                            onChange={e => {
                                                                const newData = [...formData.data];
                                                                newData[idx].link = e.target.value;
                                                                setFormData({ ...formData, data: newData });
                                                            }}
                                                        />
                                                        <button type="button" className="btn btn-ghost" onClick={() => {
                                                            const newData = [...formData.data];
                                                            newData.splice(idx, 1);
                                                            setFormData({ ...formData, data: newData });
                                                        }} style={{ color: 'var(--error)' }}><FiX /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {formData.type === 'info_form' && (
                                        <div className="form-group full-width">
                                            <div className="admin-form-grid">
                                                <div className="form-group full-width">
                                                    <label>Adresse (affichée)</label>
                                                    <textarea
                                                        placeholder="ex: 35, rue Pierre Nicole\n75005 Paris"
                                                        value={formData.data?.address || ''}
                                                        onChange={e => setFormData({ ...formData, data: { ...formData.data, address: e.target.value } })}
                                                        rows={2}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Téléphone (affiché)</label>
                                                    <input
                                                        type="text"
                                                        placeholder="ex: 01 42 36 03 50"
                                                        value={formData.data?.phone_info || ''}
                                                        onChange={e => setFormData({ ...formData, data: { ...formData.data, phone_info: e.target.value } })}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Email (affiché)</label>
                                                    <input
                                                        type="text"
                                                        placeholder="ex: info@version-net.com"
                                                        value={formData.data?.email_info || ''}
                                                        onChange={e => setFormData({ ...formData, data: { ...formData.data, email_info: e.target.value } })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {formData.type === 'map' && (
                                        <div className="form-group full-width">
                                            <label>URL d'intégration Google Maps</label>
                                            <input
                                                type="text"
                                                value={formData.btn_link}
                                                onChange={e => {
                                                    let val = e.target.value;
                                                    // Si l'utilisateur colle le code complet <iframe src="..."></iframe>
                                                    if (val.includes('<iframe')) {
                                                        const match = val.match(/src="([^"]+)"/);
                                                        if (match && match[1]) val = match[1];
                                                    }
                                                    setFormData({ ...formData, btn_link: val });
                                                }}
                                                placeholder="https://www.google.com/maps/embed?pb=..."
                                            />
                                            <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '8px', fontWeight: '600' }}>
                                                ⚠️ Attention : Vous devez utiliser le lien d'INTÉGRATION (qui commence par /maps/embed).
                                                Un lien Google Maps classique ne fonctionnera pas dans l'affichage.
                                            </p>
                                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                Allez sur Google Maps &gt; Partager &gt; Intégrer une carte &gt; Copier le contenu HTML (ou juste le lien dans 'src').
                                            </p>
                                        </div>
                                    )}

                                    {formData.type === 'service_tiles' && (
                                        <div className="form-group full-width">
                                            <div className="data-list-header">
                                                <h4>Tuiles des Services (Équipes / Départements)</h4>
                                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addDataItem('service_tiles')}>
                                                    <FiPlus /> Ajouter un bloc
                                                </button>
                                            </div>
                                            <div className="data-items-grid">
                                                {(formData.data || []).map((item, idx) => (
                                                    <div key={idx} className="data-item-card" style={{ padding: '2rem' }}>
                                                        <button type="button" className="btn-remove-item" onClick={() => removeDataItem(idx)}><FiTrash2 /></button>

                                                        <div className="item-field-row" style={{ marginBottom: '1.5rem' }}>
                                                            <label style={{ fontSize: '0.8rem', opacity: 0.7 }}>Nom du Service / Équipe</label>
                                                            <input
                                                                placeholder="ex: Service Commercial"
                                                                value={item.title}
                                                                onChange={e => updateDataItem(idx, 'title', e.target.value)}
                                                                style={{ fontWeight: 'bold' }}
                                                            />
                                                        </div>

                                                        <div className="admin-form-grid" style={{ gap: '2rem' }}>
                                                            {/* TÉLÉPHONES */}
                                                            <div>
                                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                                                                    Téléphones
                                                                    <button type="button" className="btn-text" onClick={() => addNestedDataItem(idx, 'phones')} style={{ fontSize: '0.7rem', color: '#3b82f6' }}>+ Ajouter</button>
                                                                </label>
                                                                {(item.phones || ['']).map((ph, pIdx) => (
                                                                    <div key={pIdx} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                                                                        <input
                                                                            placeholder="01 02 03 04 05"
                                                                            value={ph}
                                                                            onChange={e => updateNestedDataItem(idx, 'phones', pIdx, e.target.value)}
                                                                        />
                                                                        {item.phones?.length > 1 && (
                                                                            <button type="button" onClick={() => removeNestedDataItem(idx, 'phones', pIdx)} style={{ color: '#ef4444' }}><FiX /></button>
                                                                        )}
                                                                    </div>
                                                                ))}

                                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '1rem', display: 'block' }}>Fax</label>
                                                                <input
                                                                    placeholder="Fax"
                                                                    value={item.fax || ''}
                                                                    onChange={e => updateDataItem(idx, 'fax', e.target.value)}
                                                                />
                                                            </div>

                                                            {/* EMAILS */}
                                                            <div>
                                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                                                                    Emails
                                                                    <button type="button" className="btn-text" onClick={() => addNestedDataItem(idx, 'mails')} style={{ fontSize: '0.7rem', color: '#3b82f6' }}>+ Ajouter</button>
                                                                </label>
                                                                {(item.mails || ['']).map((ml, mIdx) => (
                                                                    <div key={mIdx} style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                                                                        <input
                                                                            placeholder="email@version-net.com"
                                                                            value={ml}
                                                                            onChange={e => updateNestedDataItem(idx, 'mails', mIdx, e.target.value)}
                                                                        />
                                                                        {item.mails?.length > 1 && (
                                                                            <button type="button" onClick={() => removeNestedDataItem(idx, 'mails', mIdx)} style={{ color: '#ef4444' }}><FiX /></button>
                                                                        )}
                                                                    </div>
                                                                ))}

                                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '1rem', display: 'block' }}>Texte complémentaire</label>
                                                                <input
                                                                    placeholder="ex: Voir rubrique Recrutement"
                                                                    value={item.description || ''}
                                                                    onChange={e => updateDataItem(idx, 'description', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label className="chk-label">
                                            <input type="checkbox" checked={formData.is_published} onChange={e => setFormData({ ...formData, is_published: e.target.checked })} />
                                            <span>Section active et visible sur le site</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={handleCancel}>Annuler</button>
                                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                    <FiSave /> {isSaving ? 'Enregistrement...' : 'Enregistrer la section'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .admin-header-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2.5rem;
                }
                
                .admin-page-title { 
                    font-size: 1.8rem; 
                    font-weight: 800; 
                    color: var(--text-main); 
                    margin: 0;
                }
                
                .admin-page-subtitle { 
                    margin: 0.5rem 0 0; 
                    color: var(--text-muted); 
                    font-size: 1rem;
                }

                .admin-content-inner { animation: fadeIn 0.4s ease-out; }
                
                .section-admin-item { 
                    display: flex; align-items: center; background: white; padding: 1.25rem; 
                    border-radius: 20px; margin-bottom: 1rem; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03); 
                    border: 1px solid #e2e8f0; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative; overflow: hidden;
                }
                
                .section-admin-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    border-color: #3b82f6;
                }

                .section-drag-handle {
                    cursor: grab;
                    padding: 8px;
                    margin-right: 0.5rem;
                    color: #94a3b8;
                    display: flex;
                    align-items: center;
                    font-size: 1.2rem;
                }

                .section-drag-controls { 
                    display: flex; flex-direction: column; align-items: center; margin-right: 1.5rem; 
                    gap: 4px; color: #94a3b8; background: #f1f5f9; padding: 8px; border-radius: 10px;
                }
                
                .section-drag-controls button { 
                    background: none; border: none; cursor: pointer; color: inherit; padding: 2px;
                    display: flex; align-items: center; justify-content: center; transition: color 0.2s;
                }
                
                .section-drag-controls button:hover:not(:disabled) { color: #3b82f6; }
                .section-drag-controls button:disabled { opacity: 0.2; cursor: default; }
                .order-num { font-size: 0.85rem; font-weight: 800; color: #475569; }
                
                .section-icon-preview {
                    width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, #eff6ff, #dbeafe);
                    color: #3b82f6; display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; margin-right: 1.5rem; border: 1px solid #bfdbfe;
                }

                .section-info { flex: 1; }
                
                .section-badge { 
                    font-size: 0.65rem; font-weight: 700; background: #f1f5f9; color: #64748b; 
                    padding: 3px 10px; border-radius: 99px; text-transform: uppercase; margin-right: 8px;
                    border: 1px solid #e2e8f0;
                }
                
                .draft-badge { 
                    font-size: 0.7rem; font-weight: 600; color: #94a3b8; display: inline-flex; 
                    align-items: center; gap: 4px; 
                }
                
                .section-name { margin: 6px 0 2px; font-size: 1.15rem; color: #1e293b; font-weight: 700; }
                .section-excerpt { margin: 0; font-size: 0.9rem; color: #64748b; }
                
                .section-item-actions { display: flex; gap: 0.75rem; }
                
                .btn-icon-round {
                    width: 38px; height: 38px; border-radius: 50%; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; justify-content: center;
                    background: white; color: #64748b; cursor: pointer;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    font-size: 1.1rem;
                }

                .btn-icon-round:hover {
                    background: #eff6ff;
                    color: #3b82f6;
                    border-color: #3b82f6;
                    transform: scale(1.1);
                }

                .btn-icon-round.is-published-btn {
                    color: #10b981;
                    border-color: #d1fae5;
                }
                .btn-icon-round.is-published-btn:hover {
                    background: #ecfdf5;
                    border-color: #10b981;
                    color: #10b981;
                }

                .btn-icon-round.is-draft-btn {
                    color: #94a3b8;
                    background: #f8fafc;
                }
                .btn-icon-round.is-draft-btn:hover {
                    background: #f1f5f9;
                    color: #334155;
                    border-color: #94a3b8;
                }

                .btn-icon-round.delete:hover {
                    background: #fef2f2;
                    color: #ef4444;
                    border-color: #fecaca;
                }

                .admin-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: flex-start; justify-content: center; padding: 2rem; overflow-y: auto; }
                .admin-modal-container { background: var(--card-bg, #fff); width: 100%; max-width: 800px; border-radius: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); border: 1px solid var(--border-subtle); overflow: hidden; }
                
                .modal-header { padding: 1.5rem 2rem; border-bottom: 1px solid var(--border-subtle); display: flex; justify-content: space-between; align-items: flex-start; }
                .modal-header h3 { margin: 0; font-size: 1.5rem; color: var(--text-main); }
                .modal-intro { margin: 4px 0 0; font-size: 0.9rem; color: var(--text-muted); }
                .close-modal { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--text-muted); padding: 4px; border-radius: 50%; display: flex; transition: all 0.2s; }
                .close-modal:hover { background: rgba(0,0,0,0.05); color: #ef4444; }
                
                .modal-body { padding: 2.5rem; max-height: 70vh; overflow-y: auto; }
                .modal-footer { padding: 1.25rem 2rem; border-top: 1px solid var(--border-subtle); display: flex; justify-content: flex-end; gap: 1rem; background: var(--bg-subtle, #f8fafc); }
                
                .admin-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
                .form-group.full-width { grid-column: span 2; }
                .form-group label { display: block; font-weight: 600; margin-bottom: 0.5rem; color: #475569; font-size: 0.9rem; }
                .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 0.75rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.95rem; }
                
                .data-list-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
                .data-item-card { background: var(--bg-subtle, #f8fafc); border: 1px solid var(--border-subtle); border-radius: 12px; padding: 1rem; position: relative; margin-bottom: 1rem; }
                .btn-remove-item { position: absolute; top: 0.5rem; right: 0.5rem; border: none; background: none; color: #ef4444; cursor: pointer; padding: 4px; border-radius: 50%; display: flex; }
                .btn-remove-item:hover { background: #fee2e2; }
                .item-row { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; }
                
                .chk-label { display: flex; align-items: center; gap: 0.75rem; cursor: pointer; font-weight: 600 !important; }
                .chk-label input { width: auto !important; }

                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                
                .fade-in { animation: fadeIn 0.3s ease-out; }
                .zoom-in { animation: zoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }

                body.theme-dark .section-admin-item { background: #1e293b; border-color: #334155; }
                body.theme-dark .section-admin-item:hover { border-color: #3b82f6; }
                body.theme-dark .section-drag-controls { background: #0f172a; color: #64748b; }
                body.theme-dark .order-num { color: #94a3b8; }
                body.theme-dark .section-icon-preview { background: linear-gradient(135deg, #1e3a8a, #1e40af); color: #60a5fa; border-color: #2563eb; }
                body.theme-dark .section-name { color: #f8fafc; }
                body.theme-dark .section-badge { background: #0f172a; border-color: #334155; color: #94a3b8; }
                body.theme-dark .btn-icon-round { background: #0f172a; border-color: #334155; color: #94a3b8; }
                body.theme-dark .btn-icon-round:hover { background: #1e3a8a; color: #60a5fa; border-color: #2563eb; }
                body.theme-dark .admin-modal-container { background: #1e293b; border-color: #334155; }
                body.theme-dark .modal-header, body.theme-dark .modal-footer { border-color: #334155; }
                body.theme-dark .modal-footer { background: #0f172a; }
                body.theme-dark .form-group label { color: #94a3b8; }
                body.theme-dark .form-group input, body.theme-dark .form-group select, body.theme-dark .form-group textarea { background: #0f172a; border-color: #334155; color: white; }
                body.theme-dark .data-item-card { background: #0f172a; border-color: #334155; }
            `}</style>
        </div>
    );
}
